/**
 * QR프린터관리 - 용지 잔량 모니터링 + 교체 리셋
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import StoreBadge from '../components/StoreBadge';
import { getPrinterPaperStatus, resetPrinterPaper, assignPrinterService } from '../services/api';
import './PrinterManagePage.css';

import { isBridgeApp } from '../utils/isBridgeApp';
const PrinterManagePage = () => {
  const navigate = useNavigate();
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resetting, setResetting] = useState(null); // printer_id being reset
  const [paperTotals, setPaperTotals] = useState({}); // {printer_id: value}

  const loadData = useCallback(async () => {
    try {
      setError(null);
      const res = await getPrinterPaperStatus();
      setPrinters(res.printers || []);
    } catch (err) {
      setError(err?.message || '프린터 정보를 불러올 수 없습니다');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000); // 30초마다 갱신
    return () => clearInterval(interval);
  }, [loadData]);

  const handleReset = async (printerId) => {
    const total = parseInt(paperTotals[printerId] || '400', 10);
    if (!total || total <= 0) return;

    setResetting(printerId);
    try {
      await resetPrinterPaper(printerId, total);
      await loadData();
    } catch (err) {
      setError(err?.message || '용지 리셋에 실패했습니다');
    } finally {
      setResetting(null);
    }
  };

  const SERVICE_LABELS = {
    'bank': '🏦 은행',
    'tools': '🔧 운영도구',
    'customer-input': '📱 고객입력',
  };

  const handleServiceChange = async (printerId, serviceType) => {
    try {
      const val = serviceType === '' ? null : serviceType;
      await assignPrinterService(printerId, val);
      await loadData();
    } catch (err) {
      setError(err?.message || '서비스 할당 변경에 실패했습니다');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'critical': return '#C62828';
      case 'warning': return '#F57C00';
      case 'empty': return '#B71C1C';
      default: return '#2E7D32';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'critical': return '긴급 교체 필요';
      case 'warning': return '교체 준비';
      case 'empty': return '용지 없음';
      default: return '정상';
    }
  };

  const getProgressPercent = (remaining, total) => {
    if (!total || total <= 0) return 0;
    return Math.max(0, Math.min(100, (remaining / total) * 100));
  };

  // 지점별 그룹핑
  const grouped = {};
  printers.forEach(p => {
    if (!grouped[p.store_id]) {
      grouped[p.store_id] = { store_name: p.store_name, printers: [] };
    }
    grouped[p.store_id].printers.push(p);
  });

  return (
    <div className="pm-page">
      <header className="pm-header">
        <button className="btn-back" onClick={() => navigate('/')}>← 메뉴</button>
        <div className="pm-header-center" onClick={() => {
          // 웹(크롬)에서는 전체화면으로 가지 않는다 — 태블릿 앱(ZlzlKiosk)에서만 (2026-08-06 요구).
          if (!isBridgeApp()) return;
          const el = document.documentElement;
          (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el)?.catch?.(() => {});
        }}>
          <h1>🖨️ QR프린터관리</h1>
          <StoreBadge />
        </div>
        <div className="pm-header-spacer" />
      </header>

      <main className="pm-content">
        {loading && <div className="pm-loading">불러오는 중...</div>}
        {error && <div className="pm-error">⚠️ {error}</div>}

        {!loading && Object.entries(grouped).map(([storeId, group]) => (
          <div key={storeId} className="pm-store-group">
            <h2 className="pm-store-name">📍 {group.store_name}</h2>
            <div className="pm-printer-list">
              {group.printers.map(printer => {
                const percent = getProgressPercent(printer.paper_remaining, printer.paper_total);
                const statusColor = getStatusColor(printer.status);
                return (
                  <div key={printer.printer_id} className={`pm-printer-card pm-status-${printer.status}`}>
                    <div className="pm-printer-info">
                      <div className="pm-printer-name">
                        🖨️ {printer.printer_name}
                        {printer.service_type && (
                          <span className="pm-service-badge">{SERVICE_LABELS[printer.service_type] || printer.service_type}</span>
                        )}
                        {!printer.service_type && <span className="pm-service-badge pm-shared">공용</span>}
                        {!printer.enabled && <span className="pm-disabled-badge">비활성</span>}
                      </div>
                      <div className="pm-printer-status" style={{ color: statusColor }}>
                        {getStatusLabel(printer.status)}
                      </div>
                    </div>

                    <div className="pm-service-row">
                      <label className="pm-service-label">서비스 할당:</label>
                      <select
                        className="pm-service-select"
                        value={printer.service_type || ''}
                        onChange={(e) => handleServiceChange(printer.printer_id, e.target.value)}
                      >
                        <option value="">공용 (전체)</option>
                        <option value="bank">🏦 은행</option>
                        <option value="tools">🔧 운영도구</option>
                        <option value="customer-input">📱 고객입력</option>
                      </select>
                    </div>

                    <div className="pm-paper-bar-container">
                      <div className="pm-paper-bar">
                        <div
                          className="pm-paper-bar-fill"
                          style={{ width: `${percent}%`, backgroundColor: statusColor }}
                        />
                      </div>
                      <div className="pm-paper-numbers">
                        <span className="pm-remaining" style={{ color: statusColor }}>
                          {printer.paper_remaining}장 남음
                        </span>
                        <span className="pm-total">/ {printer.paper_total}장</span>
                      </div>
                    </div>

                    <div className="pm-paper-detail">
                      <span>사용: {printer.paper_used}장</span>
                      {printer.paper_replaced_at && (
                        <span>교체: {printer.paper_replaced_at.split(' ')[0]}</span>
                      )}
                    </div>

                    <div className="pm-reset-row">
                      <input
                        type="number"
                        className="pm-reset-input"
                        placeholder="장수"
                        value={paperTotals[printer.printer_id] || '400'}
                        min="1"
                        max="9999"
                        onChange={(e) => setPaperTotals(prev => ({ ...prev, [printer.printer_id]: e.target.value }))}
                      />
                      <button
                        className="pm-reset-btn"
                        onClick={() => handleReset(printer.printer_id)}
                        disabled={resetting === printer.printer_id}
                      >
                        {resetting === printer.printer_id ? '리셋 중...' : '🔄 용지 교체'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {!loading && printers.length === 0 && (
          <div className="pm-empty">등록된 프린터가 없습니다</div>
        )}
      </main>
    </div>
  );
};

export default PrinterManagePage;
